import { PostStreamApplication } from '@/application/stream/posts/post';
import { NEXUS_POSTS_PER_PAGE } from '@/config/nexus';
import { NOT_FOUND_CACHED_STREAM, SKIP_FETCH_NEW_POSTS } from '@/controllers/stream/posts/post.constants';
import type {
  TClearUnreadStreamParams,
  TMarkUnreadPostsAsReadParams,
  TReadPostStreamChunkParams,
  TReadPostStreamChunkResponse,
  TStreamIdParams,
} from '@/controllers/stream/posts/posts.types';
import { captureViewerSession } from '@/controllers/tag/tag-cache.utils';
import { NetworkErrorCode } from '@/libs/error/error.codes';
import { Err } from '@/libs/error/error.factories';
import { ErrorService } from '@/libs/error/error.types';
import { isAuthorScopedContentSearchStream } from '@/models/stream/post/postStream.types';
import type { TStreamResult } from '@/services/local/stream/posts/post.types';
import { useAuthStore } from '@/stores/auth/auth.store';

// -=-=-=-=-=-=-=-=-=-=-=-=-=- POPULARITY STREAMS -=-=-=-=-=-=-=-=-=-=-=-=-=-=-=-=-=-=-=-=-=-
// If we are in engagement mode, we might not have background live updates. There is no
// identifier to fetch updates from redis. Always the top post has the max engagement
// when we render the first 10. In engagement mode, we use skip to paginate, not the score.
// -=-=-=-=-=-=-=-=-=-=-=-=-=-=-=-=-=-=-=-=-=-=-=-=-=-=-=-=-=-=-=-=-=-=-=-=-=-=-=-=--=-=--=-=-

// -=-=-=-=-=-=-=-=-=-=-=-=-= OBSERVER ID =-=-=-=-=-=-=-=-=-=-=-=-=-=-=-=-=-=-=-=-=-
// We can clone the same behavior of profile. To see an external user post stream, the url
// of streams should be /home/[external_user_id]. If we want logged user post stream,
// the url of streams should be /home.
// -=-=-=-=-=-=-=-=-=-=-=-=-=-=-=-=-=-=-=-=-=-=-=-=-=-=-=-=-=-=-=-=-=-=-=-=-=-=-=-=-

export class StreamPostsController {
  private constructor() {} // Prevent instantiation

  /**
   * Retrieves or fetches a slice of posts from a stream.
   *
   * @param params - Parameters for the stream slice request
   * @param params.streamId - Unique identifier for the stream (e.g., 'timeline:all:all', 'total_engagement:all:all')
   * @param params.streamHead - Identifier of the newest post in the current page for pagination.
   * @param params.streamTail - Resume cursor for the current page: last_post_score (timeline mode) or skip offset (engagement mode).
   * @param params.lastPostId - Post ID of the last post in the current page. We use to get the chunk of the stream from the cache.
   * @param params.limit - Limit of posts to fetch. Default is NEXUS_POSTS_PER_PAGE.
   *
   * @returns Promise resolving to stream slice response containing:
   * - nextPageIds: Array of post IDs for the current page
   * - nextCursor: opaque resume cursor for the next page (score for timeline, skip offset for engagement)
   *
   * // Get next page using the last post ID
   * const nextPage = await StreamPostsController.getOrFetchStreamSlice({
   *   streamId: 'timeline:all:all',
   *   streamTail: 1762843325,
   *   lastPostId: 'user_id:post_id',
   * });
   * // Get next page (5th page) of engagement stream
   * const nextPage = await StreamPostsController.getOrFetchStreamSlice({
   *   streamId: 'total_engagement:all:all',
   *   streamTail: 40
   * });
   * ```
   */
  static async getOrFetchStreamSlice({
    streamId,
    streamHead = SKIP_FETCH_NEW_POSTS,
    streamTail = NOT_FOUND_CACHED_STREAM,
    lastPostId,
    visiblePostIds,
    limit = NEXUS_POSTS_PER_PAGE,
    order,
  }: TReadPostStreamChunkParams): Promise<TReadPostStreamChunkResponse> {
    // selectCurrentUserPubky() throws an error when user is not authenticated;
    // access currentUserPubky directly to get null instead (unauthenticated users can view profile posts)
    const viewerId = useAuthStore.getState().currentUserPubky;
    const isCurrent = captureViewerSession();
    const { nextPageIds, cacheMissPostIds, nextCursor, reachedEnd, lastRawPostId, rawScannedCount } =
      await PostStreamApplication.getOrFetchStreamSlice({
        streamId,
        limit,
        streamHead,
        streamTail,
        lastPostId,
        visiblePostIds,
        viewerId,
        isCurrent,
        order,
      });
    if (!isCurrent()) return { nextPageIds: [], nextCursor: undefined, reachedEnd: false };
    let visibleIds = nextPageIds;
    const isAuthorScopedSearch = isAuthorScopedContentSearchStream(streamId);
    // Query nexus to get the cacheMissPostIds
    if (cacheMissPostIds.length > 0) {
      // TODO: When TTL is implemented, we can return to void
      const hydrated = await PostStreamApplication.fetchMissingPostsFromNexus({
        cacheMissPostIds,
        viewerId,
        isCurrent,
      });
      // Author-scoped search must not degrade a hydration failure into a false
      // "no results": on a cold cache every id is a miss, and the strict pass below
      // would drop them all. Fail the page instead — the feed shows its error state
      // and the cursor is not consumed, so a retry re-fetches this slice.
      if (isAuthorScopedSearch && !hydrated) {
        throw Err.network(NetworkErrorCode.CONNECTION_FAILED, 'Could not load search results', {
          service: ErrorService.Nexus,
          operation: 'StreamPostsController.getOrFetchStreamSlice',
          context: { streamId, cacheMissCount: cacheMissPostIds.length },
        });
      }
    }
    // Second-pass: cache-miss details are now resolved,
    // re-filter to catch posts that were fail-open in the first pass.
    // Only the visible page shrinks here — the raw anchor passes through untouched.
    // Strict reply classification: after hydration, an author-scoped content-search
    // result that still cannot be classified is hidden, never risked as a reply.
    // For author-scoped search the pass runs even with zero cache misses: pages served
    // from the overflow buffer report no misses, but may hold ids that were kept
    // fail-open before their relationships rows had been hydrated.
    if (cacheMissPostIds.length > 0 || (isAuthorScopedSearch && nextPageIds.length > 0)) {
      visibleIds = await PostStreamApplication.filterStreamPosts({
        streamId,
        postIds: nextPageIds,
        strictReplyClassification: true,
      });
    }
    return { nextPageIds: visibleIds, nextCursor, reachedEnd, lastRawPostId, rawScannedCount };
  }

  /**
   * Warm the local cache for posts referenced by `pubky://` URIs (e.g. a
   * collection envelope's `items`). Converts URIs to composite ids, skips
   * posts already persisted, and fetches the rest from Nexus, persisting
   * posts, authors, and file attachments.
   */
  static async fetchMissingPostsByUris({ uris }: { uris: string[] }): Promise<void> {
    if (uris.length === 0) return;
    // Access currentUserPubky directly (not selectCurrentUserPubky) so
    // unauthenticated viewers get null instead of a thrown error.
    const viewerId = useAuthStore.getState().currentUserPubky;
    const isCurrent = captureViewerSession();
    await PostStreamApplication.fetchOriginalPostsByUris({ repostedUris: uris, viewerId, isCurrent });
  }

  /**
   * The Nexus position a fresh pagination session resumes from once the cached ids are
   * exhausted: the row's persisted `tailCursor` (the `last_post_score` of the deepest page
   * fetched into it) or, for a row without one (bootstrap-seeded or written before cursors
   * were tracked), a one-time seed from the tail entry's timestamp (bookmark time for
   * bookmark streams).
   *
   * @param streamId - The ID of the post stream to query
   * @returns The resume cursor, or `NOT_FOUND_CACHED_STREAM` (0) when there is no usable cache
   */
  static async getCachedLastPostTimestamp(params: TStreamIdParams): Promise<number> {
    return await PostStreamApplication.getCachedLastPostTimestamp(params);
  }

  /**
   *
   * The "head" refers to the first post in the stream array, which represents
   * the most recently indexed post. For reply streams without a cached head,
   * returns 1 if replies exist, otherwise 0.
   *
   * @param streamId - The stream ID to get the head timestamp for
   * @returns The indexed_at timestamp of the head post, or 0 if the stream is empty or head post not found
   */
  static async getStreamHead(params: TStreamIdParams): Promise<number> {
    return await PostStreamApplication.getStreamHead(params);
  }

  /** Resolve the poll cursor, retrying unread details left missing by an earlier poll. */
  static async getOrFetchStreamHead(params: TStreamIdParams): Promise<number> {
    return PostStreamApplication.getOrFetchStreamHead({
      ...params,
      viewerId: useAuthStore.getState().currentUserPubky,
      isCurrent: captureViewerSession(),
    });
  }

  /**
   * Get local stream data from cache
   * @param streamId - The ID of the stream
   * @returns The cached stream or null if not found
   */
  static async getLocalStream(params: TStreamIdParams): Promise<TStreamResult | null> {
    return await PostStreamApplication.getLocalStream(params);
  }

  /**
   * Get the unread stream data from cache
   * @param streamId - The ID of the stream
   * @returns The unread stream or null if not found
   */
  static async getUnreadStream(params: TStreamIdParams): Promise<TStreamResult | null> {
    return await PostStreamApplication.getUnreadStream(params);
  }

  /**
   * Merge the unread stream with the post stream
   * @param params - The stream ID to merge the unread stream with the post stream
   */
  static async mergeUnreadStreamWithPostStream(params: TStreamIdParams) {
    return await PostStreamApplication.mergeUnreadStreamWithPostStream(params);
  }

  /** Merge and acknowledge exactly the posts the reader chose to open. */
  static async markUnreadPostsAsRead(params: TMarkUnreadPostsAsReadParams): Promise<void> {
    await PostStreamApplication.markUnreadPostsAsRead(params);
  }

  /**
   * Clear the selected unread IDs, or the entire stream when no IDs are supplied.
   * @param params - The stream ID to clear the unread stream for
   * @returns Array of unread IDs that were cleared
   */
  static async clearUnreadStream(params: TClearUnreadStreamParams): Promise<string[]> {
    return await PostStreamApplication.clearUnreadStream(params);
  }

  /**
   * Prepares the stream for initial load by performing cleanup operations.
   *
   * This method should be called before fetching the initial stream slice to ensure
   * the stream state is consistent. It performs the following operations:
   * 1. Clears the cache if the main head is older than the configured max age, or has no
   *    details: such a head can be neither aged nor polled from (#2608)
   * 2. Merges the unread posts into the main stream in polled order, from the first one whose
   *    details are cached and not a tombstone; the ids above it without details stay unread,
   *    since as the main head one of them would resolve no timestamp
   *    (`LocalStreamPostsService.markUnreadPostsAsReadFromResolvableHead`)
   * 3. Clears the merged posts and the tombstoned unread ids from the unread stream; with no
   *    cached main row, or an empty one, nothing is merged and the whole unread row is
   *    dropped, so the first page comes from Nexus
   *
   * This prevents race conditions where the StreamCoordinator might fetch posts
   * that are already in the main stream (due to stale unread stream head).
   *
   * @param params.streamId - The ID of the stream to prepare
   */
  static async prepareStreamForInitialLoad(params: TStreamIdParams): Promise<void> {
    await PostStreamApplication.prepareStreamForInitialLoad(params);
  }

  /**
   * Filter out deleted posts from a list of post IDs.
   * Posts without details in cache are kept (fail-open semantics).
   *
   * @param postIds - Array of post IDs to filter
   * @returns Array of post IDs that are not deleted
   */
  static async filterDeletedPosts(postIds: string[]): Promise<string[]> {
    return PostStreamApplication.filterDeletedPosts(postIds);
  }

  static async filterStreamPosts({
    streamId,
    postIds,
  }: {
    streamId: TStreamIdParams['streamId'];
    postIds: string[];
  }): Promise<string[]> {
    return PostStreamApplication.filterStreamPosts({ streamId, postIds });
  }
}
